import {
  ref as dbRef, get, onValue, push, remove, set, update, query, orderByChild, limitToLast,
} from 'firebase/database';
import { db, auth } from '../firebase';
import { compressImage, IMAGE_PRESETS, userFacingError } from './imageCompressionUtil';
import { roundMoney } from './currencyUtil';
import { logPosEvent } from './posUtil';
import { emailKey, findStaffEmailConflicts, validateStaffEmail } from './posClientUtil';
import {
  isValidDateString,
  nextStartDate,
  planEndDate,
  resolveTimeZone,
} from './subscriptionDateUtil';
import {
  POS_CLIENTS_NODE,
  POS_AUDIT_ACTIONS,
  POS_ADMIN_AUDIT_NODE,
  POS_ADMIN_AUDIT_ACTIONS,
  POS_ADMIN_SETTINGS_NODE,
  POS_APPLICATION_STATUS,
  POS_APPLICATION_REJECT_REASON_MAX_LENGTH,
  POS_CLIENT_SUBSCRIPTION_NODE,
  POS_EXTRA_STAFF_MAX,
  POS_EXTRA_STAFF_PRICE_DEFAULT,
  POS_WAREHOUSE_PRICE_DEFAULT,
  POS_PLAN_BUTTON_LABEL_MAX_LENGTH,
  POS_PLAN_FINE_PRINT_MAX_LENGTH,
  POS_PLAN_STAFF_NOTE_MAX_LENGTH,
  POS_PLAN_FREE_STAFF_MAX,
  POS_PLAN_DESCRIPTION_MAX_LENGTH,
  POS_PLAN_DURATION_MAX,
  POS_PLAN_DURATION_UNIT_KEYS,
  POS_PLAN_FEATURE_MAX_LENGTH,
  POS_PLAN_FEATURES_MAX,
  POS_PLAN_NAME_MAX_LENGTH,
  POS_PLAN_PRICE_MAX,
  POS_SUBSCRIPTION_APPLICATIONS_NODE,
  POS_SUBSCRIPTION_PLANS_NODE,
  POS_SUBSCRIPTION_RECEIPTS_NODE,
  POS_APPLICATION_HISTORY_LIMIT,
  POS_CLIENT_ACCESS_NODE,
  POS_CLIENT_DISABLED_REASON,
} from '../constants/posConst';

// Subscriptions: the plans the Website Pal sells, what each client currently
// has, and the applications clients send in to buy or renew one.
//
// Kept out of posClientUtil (the registry) and posUtil (one client's trading
// data) because it is a third concern — billing the client — and it is the
// only module that reads from all three places at once. It imports from both;
// neither imports from it, so there is no cycle.

/**
 * The acting address for an audit entry.
 *
 * @param {string} [actor]
 * @returns {string}
 */
const resolveActor = (actor) => String(actor || '').trim() || auth?.currentUser?.email || 'Unknown';

/**
 * Records an estate-level action on the administrators' own trail.
 *
 * Fire-and-forget like logPosEvent: a failed audit write is logged, never
 * allowed to fail the change it describes. The promise is returned for the
 * one caller that must let the write leave before the account changes — the
 * admin sign-out, whose entry is only writable while still signed in as the
 * administrator. It never rejects.
 *
 * @param {{action: string, entityId?: string|null, summary?: object|null, actor?: string}} entry
 * @returns {Promise<void>}
 */
export const logPosAdminEvent = ({ action, entityId = null, summary = null, actor = '' }) => (
  push(dbRef(db, POS_ADMIN_AUDIT_NODE), {
    action,
    entityId,
    summary: summary || null,
    performedBy: resolveActor(actor),
    timestamp: Date.now(),
  }).then(() => {}).catch((error) => console.error('POS admin audit write failed:', error))
);

// ---- Plans ----------------------------------------------------------------

/**
 * A plan as the screens use it, with every field present and typed.
 *
 * @param {string} id
 * @param {object} raw - as stored
 * @returns {object}
 */
const normalisePlan = (id, raw = {}) => ({
  id,
  name: String(raw.name || ''),
  price: Number(raw.price) || 0,
  durationValue: Math.max(1, Math.floor(Number(raw.durationValue) || 1)),
  durationUnit: POS_PLAN_DURATION_UNIT_KEYS.includes(raw.durationUnit) ? raw.durationUnit : 'days',
  description: String(raw.description || ''),
  features: Array.isArray(raw.features) ? raw.features.filter(Boolean).map(String) : [],
  isTrial: raw.isTrial === true,
  recommended: raw.recommended === true,
  // Absent means shown: every plan is on offer unless it was hidden on purpose.
  visibleToClients: raw.visibleToClients !== false,
  sortOrder: Number(raw.sortOrder) || 0,
  // Landing-page pricing card. All optional, so plans saved before these
  // fields existed read exactly as they did.
  //
  // compareAtPrice is the struck-through "was" price; the saving and the
  // discount badge are worked out from it rather than stored, so they can
  // never disagree with the two prices they describe.
  compareAtPrice: Number(raw.compareAtPrice) > 0 ? Number(raw.compareAtPrice) : 0,
  freeStaff: Math.max(0, Math.floor(Number(raw.freeStaff) || 0)),
  staffNote: String(raw.staffNote || ''),
  // A custom (Enterprise) plan has no price: it is sold by quote, after a
  // demo call, and can never be bought from a till.
  isCustom: raw.isCustom === true,
  // Absent means offered, except on a custom plan, which has nothing to add to.
  allowAddons: raw.isCustom === true ? false : raw.allowAddons !== false,
  buttonLabel: String(raw.buttonLabel || ''),
  finePrint: String(raw.finePrint || ''),
});

/**
 * Live list of every plan, in the order an administrator set, then by price.
 *
 * @param {(plans: object[]) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeSubscriptionPlans = (callback, onError) => onValue(
  dbRef(db, POS_SUBSCRIPTION_PLANS_NODE),
  (snapshot) => {
    const plans = Object.entries(snapshot.val() || {}).map(([id, raw]) => normalisePlan(id, raw));
    plans.sort((a, b) => (a.sortOrder - b.sortOrder) || (a.price - b.price) || a.name.localeCompare(b.name));
    callback(plans);
  },
  (error) => {
    console.error('Subscription plans listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * The plans a CLIENT may pick for themselves: visible, and not a free trial.
 *
 * Trials are excluded because they are granted, not bought — a club able to
 * re-select the free trial could renew for nothing indefinitely. Custom
 * (Enterprise) plans are excluded because they have no price to pay: they are
 * agreed by quote after a demo call.
 *
 * @param {object[]} plans
 * @returns {object[]}
 */
export const clientSelectablePlans = (plans) => (plans || [])
  .filter((plan) => plan.visibleToClients && !plan.isTrial && !plan.isCustom);

/**
 * Checks a plan before it is saved.
 *
 * @param {object} plan
 * @returns {object} the plan in its stored shape (no id)
 * @throws {Error} user-facing, naming the specific problem
 */
export const validatePlan = (plan) => {
  const name = String(plan?.name || '').trim();
  if (!name) throw userFacingError('Give the plan a name.');
  if (name.length > POS_PLAN_NAME_MAX_LENGTH) {
    throw userFacingError(`A plan name can be at most ${POS_PLAN_NAME_MAX_LENGTH} characters.`);
  }

  const isTrial = plan?.isTrial === true;
  const isCustom = !isTrial && plan?.isCustom === true;
  // A trial is free by definition, and a custom plan is priced by quote;
  // anything typed in the price is ignored rather than stored for either, so
  // it can never be charged by accident.
  const price = (isTrial || isCustom) ? 0 : Number(String(plan?.price ?? '').trim());
  if (!Number.isFinite(price) || price < 0 || price > POS_PLAN_PRICE_MAX) {
    throw userFacingError(`Enter a price between 0 and ${POS_PLAN_PRICE_MAX}.`);
  }

  const durationValue = Number(plan?.durationValue);
  if (!Number.isInteger(durationValue) || durationValue < 1 || durationValue > POS_PLAN_DURATION_MAX) {
    throw userFacingError(`Enter the plan length as a whole number from 1 to ${POS_PLAN_DURATION_MAX}.`);
  }
  if (!POS_PLAN_DURATION_UNIT_KEYS.includes(plan?.durationUnit)) {
    throw userFacingError('Choose whether the plan is counted in days or months.');
  }

  const description = String(plan?.description || '').trim();
  if (description.length > POS_PLAN_DESCRIPTION_MAX_LENGTH) {
    throw userFacingError(`Keep the description to ${POS_PLAN_DESCRIPTION_MAX_LENGTH} characters.`);
  }

  const features = (plan?.features || []).map((line) => String(line || '').trim()).filter(Boolean);
  if (features.length > POS_PLAN_FEATURES_MAX) {
    throw userFacingError(`A plan can list at most ${POS_PLAN_FEATURES_MAX} inclusions.`);
  }
  const longFeature = features.find((line) => line.length > POS_PLAN_FEATURE_MAX_LENGTH);
  if (longFeature) {
    throw userFacingError(`"${longFeature.slice(0, 30)}…" is too long — keep each inclusion to ${POS_PLAN_FEATURE_MAX_LENGTH} characters.`);
  }

  // Blank means "no compare-at price". Only meaningful on a paid plan, and
  // only when it is higher than the price — otherwise there is no saving to
  // show, and a struck-through price LOWER than the real one would mislead.
  const compareText = String(plan?.compareAtPrice ?? '').trim();
  const compareAtPrice = (isTrial || isCustom || !compareText) ? 0 : Number(compareText);
  if (!Number.isFinite(compareAtPrice) || compareAtPrice < 0 || compareAtPrice > POS_PLAN_PRICE_MAX) {
    throw userFacingError(`Enter the compare-at price as a number between 0 and ${POS_PLAN_PRICE_MAX}, or leave it blank.`);
  }
  if (compareAtPrice > 0 && compareAtPrice <= price) {
    throw userFacingError('The compare-at price must be higher than the price, or left blank.');
  }

  const freeStaffText = String(plan?.freeStaff ?? '').trim();
  const freeStaff = freeStaffText ? Number(freeStaffText) : 0;
  if (!Number.isInteger(freeStaff) || freeStaff < 0 || freeStaff > POS_PLAN_FREE_STAFF_MAX) {
    throw userFacingError(`Enter the free staff accounts as a whole number from 0 to ${POS_PLAN_FREE_STAFF_MAX}.`);
  }

  const staffNote = String(plan?.staffNote || '').trim();
  if (staffNote.length > POS_PLAN_STAFF_NOTE_MAX_LENGTH) {
    throw userFacingError(`Keep the staff note to ${POS_PLAN_STAFF_NOTE_MAX_LENGTH} characters.`);
  }
  const buttonLabel = String(plan?.buttonLabel || '').trim();
  if (buttonLabel.length > POS_PLAN_BUTTON_LABEL_MAX_LENGTH) {
    throw userFacingError(`Keep the button label to ${POS_PLAN_BUTTON_LABEL_MAX_LENGTH} characters.`);
  }
  const finePrint = String(plan?.finePrint || '').trim();
  if (finePrint.length > POS_PLAN_FINE_PRINT_MAX_LENGTH) {
    throw userFacingError(`Keep the fine print to ${POS_PLAN_FINE_PRINT_MAX_LENGTH} characters.`);
  }

  return {
    name,
    price: roundMoney(price),
    durationValue,
    durationUnit: plan.durationUnit,
    description,
    features,
    isTrial,
    recommended: plan?.recommended === true,
    visibleToClients: plan?.visibleToClients !== false,
    sortOrder: Number.isFinite(Number(plan?.sortOrder)) ? Number(plan.sortOrder) : 0,
    compareAtPrice: roundMoney(compareAtPrice),
    freeStaff,
    staffNote,
    isCustom,
    allowAddons: !isTrial && !isCustom && plan?.allowAddons !== false,
    buttonLabel,
    finePrint,
  };
};

/**
 * Creates or updates a plan.
 *
 * Editing a plan does NOT change what any client already has: a client's
 * subscription carries a copy of the plan's name and price from the day it was
 * set, so a price rise applies to the next purchase, not retroactively.
 *
 * @param {object} plan - with `id` to update, without to create
 * @param {string} [actor]
 * @returns {Promise<string>} the plan id
 */
export const saveSubscriptionPlan = async (plan, actor = '') => {
  const clean = validatePlan(plan);
  const now = new Date().toISOString();
  const isEdit = Boolean(plan?.id);
  const id = isEdit ? plan.id : push(dbRef(db, POS_SUBSCRIPTION_PLANS_NODE)).key;

  await set(dbRef(db, `${POS_SUBSCRIPTION_PLANS_NODE}/${id}`), {
    ...clean,
    createdAt: plan?.createdAt || now,
    updatedAt: now,
  });

  logPosAdminEvent({
    actor,
    action: isEdit ? POS_ADMIN_AUDIT_ACTIONS.PLAN_UPDATED : POS_ADMIN_AUDIT_ACTIONS.PLAN_CREATED,
    entityId: id,
    summary: {
      name: clean.name,
      price: clean.price,
      duration: `${clean.durationValue} ${clean.durationUnit}`,
      isTrial: clean.isTrial,
      isCustom: clean.isCustom,
      compareAtPrice: clean.compareAtPrice,
      freeStaff: clean.freeStaff,
      visibleToClients: clean.visibleToClients,
    },
  });
  return id;
};

/**
 * Removes a plan. Clients already on it keep their dates — see saveSubscriptionPlan.
 *
 * @param {object} plan
 * @param {string} [actor]
 * @returns {Promise<void>}
 */
export const deleteSubscriptionPlan = async (plan, actor = '') => {
  if (!plan?.id) throw userFacingError('That plan has no id and cannot be removed.');
  await remove(dbRef(db, `${POS_SUBSCRIPTION_PLANS_NODE}/${plan.id}`));
  logPosAdminEvent({
    actor,
    action: POS_ADMIN_AUDIT_ACTIONS.PLAN_DELETED,
    entityId: plan.id,
    summary: { name: plan.name, price: plan.price },
  });
};

// ---- Admin settings -------------------------------------------------------

/** What Admin Settings holds before anyone has saved it. */
export const DEFAULT_ADMIN_SETTINGS = {
  paymentQrUrl: '',
  paymentAccountName: '',
  paymentAccountNumber: '',
  paymentInstructions: '',
  extraStaffPrice: POS_EXTRA_STAFF_PRICE_DEFAULT,
  warehousePrice: POS_WAREHOUSE_PRICE_DEFAULT,
};

/**
 * Live Admin Settings, merged over the defaults.
 *
 * @param {(settings: object) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeAdminSettings = (callback, onError) => onValue(
  dbRef(db, POS_ADMIN_SETTINGS_NODE),
  (snapshot) => callback({ ...DEFAULT_ADMIN_SETTINGS, ...(snapshot.val() || {}) }),
  (error) => {
    console.error('Admin settings listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * Saves Admin Settings.
 *
 * @param {object} details
 * @param {File|null} [details.qrFile] - a new QR image, compressed here
 * @param {boolean} [details.clearQr] - remove the stored QR
 * @param {string} details.paymentAccountName
 * @param {string} details.paymentAccountNumber
 * @param {string} details.paymentInstructions
 * @param {number|string} details.extraStaffPrice
 * @param {number|string} details.warehousePrice - the landing page's warehouse add-on
 * @param {object} existing - the settings as loaded
 * @param {string} [actor]
 * @returns {Promise<object>} what was saved
 */
export const saveAdminSettings = async ({
  qrFile = null, clearQr = false, paymentAccountName, paymentAccountNumber, paymentInstructions, extraStaffPrice,
  warehousePrice,
}, existing = DEFAULT_ADMIN_SETTINGS, actor = '') => {
  const price = Number(String(extraStaffPrice ?? '').trim());
  if (!Number.isFinite(price) || price < 0 || price > POS_PLAN_PRICE_MAX) {
    throw userFacingError('Enter the price of an extra staff account as a number, 0 or more.');
  }
  const warehouse = Number(String(warehousePrice ?? '').trim());
  if (!Number.isFinite(warehouse) || warehouse < 0 || warehouse > POS_PLAN_PRICE_MAX) {
    throw userFacingError('Enter the price of the warehouse add-on as a number, 0 or more.');
  }

  let paymentQrUrl = clearQr ? '' : (existing?.paymentQrUrl || '');
  if (qrFile) {
    // The QR preset keeps edges sharp: a QR that compresses into mush cannot
    // be scanned, which is worse than no QR at all.
    const { dataUrl } = await compressImage(qrFile, IMAGE_PRESETS.QR);
    paymentQrUrl = dataUrl;
  }

  const clean = {
    paymentQrUrl,
    paymentAccountName: String(paymentAccountName || '').trim(),
    paymentAccountNumber: String(paymentAccountNumber || '').trim(),
    paymentInstructions: String(paymentInstructions || '').trim(),
    extraStaffPrice: roundMoney(price),
    warehousePrice: roundMoney(warehouse),
    updatedAt: new Date().toISOString(),
  };
  await set(dbRef(db, POS_ADMIN_SETTINGS_NODE), clean);

  logPosAdminEvent({
    actor,
    action: POS_ADMIN_AUDIT_ACTIONS.ADMIN_SETTINGS_UPDATED,
    entityId: 'adminSettings',
    summary: {
      qrChanged: Boolean(qrFile) || (clearQr && Boolean(existing?.paymentQrUrl)),
      paymentAccountName: clean.paymentAccountName,
      extraStaffPrice: clean.extraStaffPrice,
      warehousePrice: clean.warehousePrice,
    },
  });
  return clean;
};

// ---- A client's subscription ---------------------------------------------

/**
 * Live subscription of one client. `null` when it has none.
 *
 * @param {string} clientId
 * @param {(subscription: object|null) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeClientSubscription = (clientId, callback, onError) => onValue(
  dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/${POS_CLIENT_SUBSCRIPTION_NODE}`),
  (snapshot) => callback(snapshot.exists() ? snapshot.val() : null),
  (error) => {
    console.error('Client subscription listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * One client's subscription, read once. `null` when it has none.
 *
 * @param {string} clientId
 * @returns {Promise<object|null>}
 */
export const fetchClientSubscription = async (clientId) => {
  const snapshot = await get(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/${POS_CLIENT_SUBSCRIPTION_NODE}`));
  return snapshot.exists() ? snapshot.val() : null;
};

/**
 * Builds the stored subscription record from a plan and dates.
 *
 * The plan's name, price and length are COPIED in, so the record still reads
 * correctly after the plan is renamed, repriced or deleted.
 *
 * @param {object} plan
 * @param {{startDate: string, endDate: string, extraStaff?: number, source: string, applicationId?: string, actor?: string}} details
 * @returns {object}
 */
const buildSubscriptionRecord = (plan, {
  startDate, endDate, extraStaff = 0, source, applicationId = null, actor = '',
}) => ({
  planId: plan.id || plan.planId || '',
  planName: plan.name || plan.planName || '',
  isTrial: plan.isTrial === true,
  price: Number(plan.price) || 0,
  durationValue: plan.durationValue,
  durationUnit: plan.durationUnit,
  startDate,
  endDate,
  extraStaff,
  source,
  applicationId,
  updatedAt: new Date().toISOString(),
  updatedBy: resolveActor(actor),
});

/**
 * Sets (or clears) a client's subscription from the admin form.
 *
 * @param {string} clientId
 * @param {{plan: object, startDate: string, endDate: string}|null} choice -
 *   null removes the subscription entirely.
 * @param {{existing?: object|null, actor?: string}} [options]
 * @returns {Promise<object|null>} what was saved
 * @throws {Error} user-facing, on a missing plan or impossible dates
 */
export const saveClientSubscription = async (clientId, choice, { existing = null, actor = '' } = {}) => {
  const path = `${POS_CLIENTS_NODE}/${clientId}/${POS_CLIENT_SUBSCRIPTION_NODE}`;

  if (!choice) {
    if (!existing) return null;
    await remove(dbRef(db, path));
    logPosEvent({
      clientId,
      actor,
      action: POS_AUDIT_ACTIONS.SUBSCRIPTION_SET,
      entityId: clientId,
      summary: { plan: 'None', before: { plan: existing.planName, ends: existing.endDate } },
    });
    return null;
  }

  const { plan, startDate, endDate } = choice;
  if (!plan) throw userFacingError('Choose a subscription plan, or set it to "No subscription".');
  if (!isValidDateString(startDate)) throw userFacingError('Enter a valid start date for the subscription.');
  if (!isValidDateString(endDate)) throw userFacingError('Enter a valid end date for the subscription.');
  if (endDate < startDate) throw userFacingError('The subscription cannot end before it starts.');

  const record = buildSubscriptionRecord(plan, {
    startDate,
    endDate,
    // Extra staff bought through an application stay with the period they
    // were bought for, when an admin only adjusts the dates.
    extraStaff: existing?.planId === plan.id ? (existing?.extraStaff || 0) : 0,
    source: 'admin',
    actor,
  });
  await set(dbRef(db, path), record);

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.SUBSCRIPTION_SET,
    entityId: clientId,
    summary: {
      plan: record.planName,
      starts: record.startDate,
      ends: record.endDate,
      ...(existing ? { before: { plan: existing.planName, starts: existing.startDate, ends: existing.endDate } } : {}),
    },
  });
  return record;
};

/**
 * Whether the admin form's choice differs from what is stored — so saving an
 * unrelated field does not rewrite the subscription and log a change that
 * never happened.
 *
 * @param {object|null} existing
 * @param {{plan: object, startDate: string, endDate: string}|null} choice
 * @returns {boolean}
 */
export const subscriptionChoiceChanged = (existing, choice) => {
  if (!existing && !choice) return false;
  if (!existing || !choice) return true;
  return existing.planId !== choice.plan?.id
    || existing.startDate !== choice.startDate
    || existing.endDate !== choice.endDate;
};

// ---- Client access (disable / enable) -------------------------------------

/**
 * Live access record of one client: `{ disabled: true, ... }` when an
 * administrator has shut its till, `null` when it is open.
 *
 * @param {string} clientId
 * @param {(access: object|null) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeClientAccess = (clientId, callback, onError) => onValue(
  dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/${POS_CLIENT_ACCESS_NODE}`),
  (snapshot) => callback(snapshot.exists() ? snapshot.val() : null),
  (error) => {
    console.error('Client access listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * Disables or re-enables a client's till.
 *
 * Disabling writes a record; enabling DELETES it, so an open till is simply
 * the absence of the node and there is only one way to spell "open".
 *
 * Nothing else about the client is touched — its products, sales, staff and
 * subscription all stay exactly as they were, ready for the day it renews.
 *
 * @param {object} client - from subscribePosClients (id, name, subscription)
 * @param {boolean} disabled
 * @param {string} [actor]
 * @returns {Promise<void>}
 */
export const setClientDisabled = async (client, disabled, actor = '') => {
  if (!client?.id) throw userFacingError('That client has no id.');
  const path = `${POS_CLIENTS_NODE}/${client.id}/${POS_CLIENT_ACCESS_NODE}`;
  const by = resolveActor(actor);

  if (disabled) {
    await set(dbRef(db, path), {
      disabled: true,
      reason: POS_CLIENT_DISABLED_REASON,
      disabledAt: Date.now(),
      disabledBy: by,
    });
  } else {
    await remove(dbRef(db, path));
  }

  const summary = {
    client: client.name || client.id,
    plan: client.subscription?.planName || 'None',
    ended: client.subscription?.endDate || '',
  };
  logPosEvent({
    clientId: client.id,
    actor: by,
    action: disabled ? POS_AUDIT_ACTIONS.CLIENT_DISABLED : POS_AUDIT_ACTIONS.CLIENT_ENABLED,
    entityId: client.id,
    summary,
  });
  logPosAdminEvent({
    actor: by,
    action: disabled ? POS_ADMIN_AUDIT_ACTIONS.CLIENT_DISABLED : POS_ADMIN_AUDIT_ACTIONS.CLIENT_ENABLED,
    entityId: client.id,
    summary,
  });
};

// ---- Applications ---------------------------------------------------------

/**
 * What an application costs.
 *
 * THE PLAN'S FREE STAFF ACCOUNTS COME FIRST. A plan can include staff
 * accounts (Free staff accounts on Admin -> Subscription Plans); the first
 * that many accounts a client asks for are covered by it, and only those
 * beyond it are charged at the per-account price.
 *
 * @param {object} plan - with `price` and `freeStaff`
 * @param {number} staffCount - how many staff accounts the client asked for
 * @param {number} extraStaffPrice - per charged account
 * @returns {{planPrice: number, freeStaffCount: number, paidStaffCount: number, extraStaffTotal: number, total: number}}
 *   freeStaffCount: accounts covered by the plan; paidStaffCount: accounts charged
 */
export const computeApplicationTotal = (plan, staffCount, extraStaffPrice) => {
  const planPrice = Number(plan?.price) || 0;
  const requested = Math.max(0, Math.floor(Number(staffCount) || 0));
  const allowance = Math.max(0, Math.floor(Number(plan?.freeStaff) || 0));
  const freeStaffCount = Math.min(requested, allowance);
  const paidStaffCount = requested - freeStaffCount;
  const extraStaffTotal = roundMoney(paidStaffCount * (Number(extraStaffPrice) || 0));
  return {
    planPrice, freeStaffCount, paidStaffCount, extraStaffTotal, total: roundMoney(planPrice + extraStaffTotal),
  };
};

/**
 * Cleans and checks the extra staff addresses a client typed.
 *
 * Duplicates collapse to one — the same person twice is one account.
 *
 * @param {string} clientId
 * @param {string[]} emails
 * @returns {string[]} lower-cased, unique, non-blank
 * @throws {Error} user-facing, on a malformed address or too many
 */
export const cleanExtraStaffEmails = (clientId, emails) => {
  const unique = [...new Set((emails || [])
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .map((value) => validateStaffEmail(clientId, value)))];
  if (unique.length > POS_EXTRA_STAFF_MAX) {
    throw userFacingError(`One application can add at most ${POS_EXTRA_STAFF_MAX} staff accounts.`);
  }
  return unique;
};

/**
 * Sends a client's application — plan, extra staff and proof of payment — for
 * review.
 *
 * The application and its receipt are written in ONE multi-path update, so
 * there is never an application without its receipt or a receipt nobody can
 * find.
 *
 * @param {object} details
 * @param {string} details.clientId
 * @param {string} details.clientName
 * @param {object} details.plan
 * @param {string[]} details.extraStaffEmails
 * @param {number} details.extraStaffPrice
 * @param {File} details.receiptFile
 * @param {string} details.submittedBy - the staff address at the till
 * @returns {Promise<object>} the saved application, with its id
 */
export const submitSubscriptionApplication = async ({
  clientId, clientName, plan, extraStaffEmails, extraStaffPrice, receiptFile, submittedBy,
}) => {
  if (!clientId) throw userFacingError('This till does not know which client it belongs to.');
  if (!plan?.id) throw userFacingError('Choose a subscription plan first.');
  if (!receiptFile) throw userFacingError('Upload a photo or screenshot of your payment receipt.');

  const emails = cleanExtraStaffEmails(clientId, extraStaffEmails);
  const {
    planPrice, freeStaffCount, paidStaffCount, extraStaffTotal, total,
  } = computeApplicationTotal(plan, emails.length, extraStaffPrice);
  // RECEIPT preset: large enough to read a reference number off, small enough
  // that the admin panel loads it quickly.
  const { dataUrl } = await compressImage(receiptFile, IMAGE_PRESETS.RECEIPT);

  const id = push(dbRef(db, `${POS_SUBSCRIPTION_APPLICATIONS_NODE}/${clientId}`)).key;
  const application = {
    clientId,
    clientName: String(clientName || clientId),
    planId: plan.id,
    planName: plan.name,
    planPrice,
    durationValue: plan.durationValue,
    durationUnit: plan.durationUnit,
    extraStaffEmails: emails,
    // How many of those accounts the plan covers, and how many were charged —
    // recorded so the review screen shows the same split the client paid by.
    freeStaffCount,
    paidStaffCount,
    extraStaffPrice: Number(extraStaffPrice) || 0,
    extraStaffTotal,
    total,
    status: POS_APPLICATION_STATUS.PENDING,
    submittedBy: resolveActor(submittedBy),
    submittedAt: Date.now(),
  };

  await update(dbRef(db), {
    [`${POS_SUBSCRIPTION_APPLICATIONS_NODE}/${clientId}/${id}`]: application,
    [`${POS_SUBSCRIPTION_RECEIPTS_NODE}/${clientId}/${id}`]: dataUrl,
  });

  logPosEvent({
    clientId,
    actor: submittedBy,
    action: POS_AUDIT_ACTIONS.SUBSCRIPTION_APPLIED,
    entityId: id,
    summary: {
      plan: application.planName,
      extraStaff: emails.length,
      freeStaff: freeStaffCount,
      total,
    },
  });
  return { id, ...application };
};

/**
 * Live list of one client's most recent applications, newest first.
 *
 * Small on purpose — the till only needs to know whether one is pending and
 * how the last one ended.
 *
 * @param {string} clientId
 * @param {(applications: object[]) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeClientApplications = (clientId, callback, onError) => onValue(
  query(dbRef(db, `${POS_SUBSCRIPTION_APPLICATIONS_NODE}/${clientId}`), orderByChild('submittedAt'), limitToLast(POS_APPLICATION_HISTORY_LIMIT)),
  (snapshot) => {
    const list = [];
    snapshot.forEach((child) => { list.push({ id: child.key, ...child.val() }); });
    list.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
    callback(list);
  },
  (error) => {
    console.error('Client applications listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * Live list of every client's applications, newest first. Admin-only.
 *
 * @param {(applications: object[]) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeAllApplications = (callback, onError) => onValue(
  dbRef(db, POS_SUBSCRIPTION_APPLICATIONS_NODE),
  (snapshot) => {
    const list = [];
    snapshot.forEach((clientNode) => {
      clientNode.forEach((child) => {
        list.push({ id: child.key, ...child.val(), clientId: clientNode.key });
      });
    });
    list.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
    callback(list);
  },
  (error) => {
    console.error('Applications listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * The receipt image attached to one application. Admin-only.
 *
 * @param {string} clientId
 * @param {string} applicationId
 * @returns {Promise<string>} a data URL, or '' when there is none
 */
export const fetchApplicationReceipt = async (clientId, applicationId) => {
  const snapshot = await get(dbRef(db, `${POS_SUBSCRIPTION_RECEIPTS_NODE}/${clientId}/${applicationId}`));
  return snapshot.exists() ? String(snapshot.val() || '') : '';
};

/**
 * The dates an application WOULD run for if approved now, and the timezone
 * they are counted in.
 *
 * Shared by the review screen's preview and by the approval itself, so what
 * the administrator is shown is what is actually written.
 *
 * @param {object} application
 * @returns {Promise<{startDate: string, endDate: string, timeZone: string, current: object|null}>}
 */
export const planApprovalDates = async (application) => {
  const [profileSnap, current] = await Promise.all([
    get(dbRef(db, `${POS_CLIENTS_NODE}/${application.clientId}/profile/timezone`)),
    fetchClientSubscription(application.clientId),
  ]);
  const timeZone = resolveTimeZone(profileSnap.val());
  const startDate = nextStartDate(current, timeZone);
  const endDate = planEndDate(startDate, application.durationValue, application.durationUnit);
  return { startDate, endDate, timeZone, current };
};

/**
 * Approves an application: marks it approved, activates the plan on the
 * client, and adds the extra staff accounts it paid for — all in ONE write.
 *
 * Extra staff that are already on this client are simply kept; any already
 * registered to a DIFFERENT client are skipped and reported, because one
 * address cannot sign in at two clubs' tills.
 *
 * @param {object} application
 * @param {string} [actor]
 * A client that had been DISABLED is re-enabled by the same write.
 *
 * @returns {Promise<{subscription: object, addedStaff: string[], skippedStaff: Array<{email: string, reason: string}>, reEnabled: boolean, clientEmail: string}>}
 *   `clientEmail` is the address on the client's profile ('' when none), for
 *   the approval email
 * @throws {Error} user-facing, when the application is no longer pending
 */
export const approveSubscriptionApplication = async (application, actor = '') => {
  const { clientId, id } = application;
  const appPath = `${POS_SUBSCRIPTION_APPLICATIONS_NODE}/${clientId}/${id}`;

  // Re-read rather than trusting the panel: another administrator may have
  // reviewed it a moment ago, and approving twice would extend the plan twice.
  const fresh = await get(dbRef(db, appPath));
  if (!fresh.exists() || fresh.val()?.status !== POS_APPLICATION_STATUS.PENDING) {
    throw userFacingError('This application has already been reviewed.');
  }

  const { startDate, endDate } = await planApprovalDates(application);
  const accessSnap = await get(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/${POS_CLIENT_ACCESS_NODE}`));
  const reEnabled = accessSnap.exists() && accessSnap.val()?.disabled === true;

  const [staffSnap, profileEmailSnap] = await Promise.all([
    get(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/staff`)),
    // Read here so the caller can email the client without a second trip.
    get(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/profile/email`)),
  ]);
  const existingStaff = new Set(Object.values(staffSnap.val() || {})
    .map((entry) => String(entry?.email || '').trim().toLowerCase()));
  const requested = (application.extraStaffEmails || []).map((value) => String(value).trim().toLowerCase());
  const fresher = requested.filter((email) => !existingStaff.has(email));
  const conflicts = await findStaffEmailConflicts(clientId, fresher);
  const conflictMap = new Map(conflicts.map((entry) => [entry.email, entry.ownerName]));

  const addedStaff = fresher.filter((email) => !conflictMap.has(email));
  const skippedStaff = [
    ...requested.filter((email) => existingStaff.has(email)).map((email) => ({ email, reason: 'already staff of this client' })),
    ...fresher.filter((email) => conflictMap.has(email)).map((email) => ({ email, reason: `already staff of ${conflictMap.get(email)}` })),
  ];

  const subscription = buildSubscriptionRecord({
    planId: application.planId,
    planName: application.planName,
    price: application.planPrice,
    durationValue: application.durationValue,
    durationUnit: application.durationUnit,
  }, {
    startDate,
    endDate,
    extraStaff: requested.length,
    source: 'application',
    applicationId: id,
    actor,
  });

  const reviewedBy = resolveActor(actor);
  const updates = {
    [`${appPath}/status`]: POS_APPLICATION_STATUS.APPROVED,
    [`${appPath}/reviewedBy`]: reviewedBy,
    [`${appPath}/reviewedAt`]: Date.now(),
    [`${appPath}/activatedStart`]: startDate,
    [`${appPath}/activatedEnd`]: endDate,
    [`${POS_CLIENTS_NODE}/${clientId}/${POS_CLIENT_SUBSCRIPTION_NODE}`]: subscription,
    // A paid renewal re-opens a till that was disabled for not renewing, in
    // the same write — a club that has just paid should not also have to
    // wait for someone to remember to switch it back on.
    [`${POS_CLIENTS_NODE}/${clientId}/${POS_CLIENT_ACCESS_NODE}`]: null,
  };
  addedStaff.forEach((email) => {
    const key = emailKey(email);
    if (key) updates[`${POS_CLIENTS_NODE}/${clientId}/staff/${key}`] = { email };
  });

  await update(dbRef(db), updates);

  const summary = {
    plan: application.planName,
    starts: startDate,
    ends: endDate,
    total: application.total,
    staffAdded: addedStaff,
    ...(reEnabled ? { tillReEnabled: true } : {}),
  };
  logPosEvent({ clientId, actor: reviewedBy, action: POS_AUDIT_ACTIONS.SUBSCRIPTION_APPROVED, entityId: id, summary });
  logPosAdminEvent({
    actor: reviewedBy,
    action: POS_ADMIN_AUDIT_ACTIONS.APPLICATION_APPROVED,
    entityId: id,
    summary: { client: clientId, ...summary },
  });

  const clientEmail = String(profileEmailSnap.val() || '').trim();
  return {
    subscription, addedStaff, skippedStaff, reEnabled, clientEmail,
  };
};

/**
 * Rejects an application, with a reason the client will read on their till.
 *
 * @param {object} application
 * @param {string} reason
 * @param {string} [actor]
 * @returns {Promise<void>}
 * @throws {Error} user-facing, on a blank reason or an already-reviewed application
 */
export const rejectSubscriptionApplication = async (application, reason, actor = '') => {
  const clean = String(reason || '').trim();
  if (!clean) throw userFacingError('Give a reason, so the client knows what to fix.');
  if (clean.length > POS_APPLICATION_REJECT_REASON_MAX_LENGTH) {
    throw userFacingError(`Keep the reason to ${POS_APPLICATION_REJECT_REASON_MAX_LENGTH} characters.`);
  }

  const { clientId, id } = application;
  const appPath = `${POS_SUBSCRIPTION_APPLICATIONS_NODE}/${clientId}/${id}`;
  const fresh = await get(dbRef(db, appPath));
  if (!fresh.exists() || fresh.val()?.status !== POS_APPLICATION_STATUS.PENDING) {
    throw userFacingError('This application has already been reviewed.');
  }

  const reviewedBy = resolveActor(actor);
  await update(dbRef(db, appPath), {
    status: POS_APPLICATION_STATUS.REJECTED,
    rejectReason: clean,
    reviewedBy,
    reviewedAt: Date.now(),
  });

  const summary = { plan: application.planName, total: application.total, reason: clean };
  logPosEvent({ clientId, actor: reviewedBy, action: POS_AUDIT_ACTIONS.SUBSCRIPTION_REJECTED, entityId: id, summary });
  logPosAdminEvent({
    actor: reviewedBy,
    action: POS_ADMIN_AUDIT_ACTIONS.APPLICATION_REJECTED,
    entityId: id,
    summary: { client: clientId, ...summary },
  });
};
